/**
 * IndustryPicker — one-click template setup.
 *
 * Groups all 50 industries by category, shows what each template ships with
 * (resources, services, custom fields, reminder cadence) and fires
 * POST /api/booking/setup on click.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Stethoscope, HeartPulse, Droplets, Eye, Activity, Bone, Brain, TestTube,
  PawPrint, Scissors, Home, Route, Sparkle, Palette, Circle, Dumbbell, Flower, Flower2,
  Trophy, Waves, GraduationCap, Music, Car, Scale, Calculator, Briefcase, Compass, Camera,
  Utensils, Bed, KeyRound, PartyPopper, Landmark, SprayCan, TreePine, Thermometer,
  Building, Laptop, Wrench, Hand, Heart, Search, Check,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';

const ICON_MAP = {
  Sparkles, Stethoscope, HeartPulse, Droplets, Eye, Activity, Bone, Brain, TestTube,
  PawPrint, Scissors, Home, Route, Sparkle, Palette, Circle, Dumbbell, Flower, Flower2,
  Trophy, Waves, GraduationCap, Music, Car, Scale, Calculator, Briefcase, Compass, Camera,
  Utensils, Bed, KeyRound, PartyPopper, Landmark, SprayCan, TreePine, Thermometer,
  Building, Laptop, Wrench, Hand, Heart,
};

const API = process.env.REACT_APP_BACKEND_URL;

export default function IndustryPicker({ onSetupComplete }) {
  const [data, setData] = useState({ categories: [], industries: [] });
  const [search, setSearch] = useState('');
  const [installing, setInstalling] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/booking/industries`).then((r) => setData(r.data)).catch(() => {});
  }, []);

  const install = async (key) => {
    setInstalling(key);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/booking/setup`, { industry_key: key }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Template installed — resources, services, and reminders are ready.');
      onSetupComplete?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Setup failed');
    } finally {
      setInstalling(null);
    }
  };

  const filtered = data.industries.filter(
    (i) => !search || i.label.toLowerCase().includes(search.toLowerCase()) || i.category.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="industry-picker">
      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
        <Input
          className="pl-10"
          placeholder="Search 50+ industries — dental, salon, pet, real estate…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="industry-search"
        />
      </div>

      {data.categories.map((cat) => {
        const rows = filtered.filter((i) => i.category === cat);
        if (rows.length === 0) return null;
        return (
          <div key={cat}>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{cat}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {rows.map((i) => {
                const Icon = ICON_MAP[i.icon] || Sparkles;
                return (
                  <Card
                    key={i.key}
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => install(i.key)}
                    data-testid={`industry-${i.key}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                          <Icon className="h-5 w-5 text-emerald-brand" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-text-primary">{i.label}</div>
                          <div className="text-xs text-gray-medium mt-1">
                            {i.resource_count} resources · {i.service_count} services · {i.custom_field_count} fields
                          </div>
                        </div>
                        {installing === i.key ? (
                          <div className="text-xs text-emerald-brand">Installing…</div>
                        ) : (
                          <Button size="sm" variant="ghost" className="h-8 gap-1 text-xs">
                            <Check className="h-3.5 w-3.5" />
                            Use
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}

      {filtered.length === 0 && (
        <p className="text-sm text-gray-medium text-center py-12">No industries match &ldquo;{search}&rdquo;.</p>
      )}
    </div>
  );
}
